/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import equal from 'lodash.isequal';
import {GenericObject} from '../types/entities';
import {isSame} from './DateUtil';
import {isEqual, notEqual} from './StringUtil';

export const isStringEqual    = isEqual;
export const isStringNotEqual = notEqual;
export const isSameDate       = isSame;

type IsDeepEqualParam = (
  GenericObject | IsDeepEqualParam[] | Map<IsDeepEqualParam, IsDeepEqualParam> | Set<IsDeepEqualParam>
);

/**
 * Provides a deep equality check against two items, returning true if both items are
 * equal, otherwise, returns false.
 *
 * ```typescript
 *
 *   import {isDeepEqual} from '@ion/core/src/utils/Equality';
 *
 *   const isSame = isDeepEqual([{a: 'a'}], [{a: 'a'}]); // true
 *   ...
 *   const isSame = isDeepEqual([{a: 'a'}], [{a: 'A'}]); // false
 *
 * ```
 *
 * @param a {IsDeepEqualParam} The item to compare against the second item.
 * @param b {IsDeepEqualParam} The item to compare against the first item.
 */
export const isDeepEqual = (a: IsDeepEqualParam, b: IsDeepEqualParam): boolean => equal(a, b);

/**
 * Provides a deep equality check against two items, returning true if both items are
 * not equal, otherwise, returns false. This function is the inverse of isDeepEqual.
 *
 * ```typescript
 *
 *   import {notDeepEqual} from '@ion/core/src/utils/Equality';
 *
 *   const hasChanges = notDeepEqual([{a: 'a'}], [{a: 'A'}]); // true
 *   ...
 *   const hasChanges = notDeepEqual([{a: 'a'}], [{a: 'a'}]); // false
 *
 * ```
 *
 * @param a {IsDeepEqualParam} The item to compare against the second item.
 * @param b {IsDeepEqualParam} The item to compare against the first item.
 */
export const notDeepEqual = (a: IsDeepEqualParam, b: IsDeepEqualParam): boolean => !isDeepEqual(a, b);
